<script setup lang="ts">
import { computed } from 'vue'
import type { TradeProposal } from '@/api/client'

const props = defineProps<{
  tradeProposal: TradeProposal
}>()

const ACTION_META: Record<string, { label: string; type: 'success' | 'warning' | 'danger' }> = {
  buy: { label: '买入', type: 'success' },
  hold: { label: '观望', type: 'warning' },
  sell: { label: '卖出/规避', type: 'danger' },
}

const actionMeta = computed(() => ACTION_META[props.tradeProposal.action] || { label: props.tradeProposal.action, type: 'warning' as const })

function fmt(v: number | null | undefined): string {
  return typeof v === 'number' ? v.toFixed(2) : '-'
}

const entryRange = computed(() => {
  const p = props.tradeProposal
  if (p.entry_low == null && p.entry_high == null) return '-'
  if (p.entry_low == null) return `≤ ${fmt(p.entry_high)}`
  if (p.entry_high == null) return `≥ ${fmt(p.entry_low)}`
  return `${fmt(p.entry_low)} ~ ${fmt(p.entry_high)}`
})
</script>

<template>
  <el-card shadow="never" class="trader-panel">
    <template #header>
      <div class="header-bar">
        <span class="title">🎯 交易执行官提案 (Trader Proposal)</span>
        <el-tag :type="actionMeta.type" effect="dark" size="small">{{ actionMeta.label }}</el-tag>
      </div>
    </template>

    <!-- 绝对价格执行矩阵 -->
    <div class="price-matrix">
      <div class="pm-cell pm-entry">
        <div class="pm-label">入场区间 (Entry Range)</div>
        <div class="pm-value">{{ entryRange }}</div>
      </div>
      <div class="pm-cell pm-stop">
        <div class="pm-label">物理止损价 (Stop Loss)</div>
        <div class="pm-value">{{ fmt(tradeProposal.stop_loss) }}</div>
      </div>
      <div class="pm-cell pm-t1">
        <div class="pm-label">目标一 (Target 1)</div>
        <div class="pm-value">{{ fmt(tradeProposal.target_1) }}</div>
      </div>
      <div class="pm-cell pm-t2">
        <div class="pm-label">目标二 (Target 2)</div>
        <div class="pm-value">{{ fmt(tradeProposal.target_2) }}</div>
      </div>
    </div>

    <div class="meta-row">
      <span class="meta-item">
        盈亏比 (R/R):
        <strong :style="{ color: (tradeProposal.risk_reward_ratio ?? 0) >= 2 ? 'var(--success)' : (tradeProposal.risk_reward_ratio ?? 0) >= 1.5 ? 'var(--warning)' : 'var(--danger)' }">
          {{ tradeProposal.risk_reward_ratio != null ? `1:${tradeProposal.risk_reward_ratio.toFixed(1)}` : '-' }}
        </strong>
      </span>
      <span class="meta-item">建议仓位: <strong>{{ tradeProposal.position_sizing || '-' }}</strong></span>
      <span class="meta-item">持有周期: <strong>{{ tradeProposal.holding_period || '-' }}</strong></span>
    </div>

    <div v-if="tradeProposal.reasoning" class="reasoning">
      <strong>提案理由:</strong> {{ tradeProposal.reasoning }}
    </div>
    <el-alert
      v-if="tradeProposal._failed"
      title="提案生成降级: 由兜底逻辑按裁决方向生成, 点位字段为空"
      type="warning"
      :closable="false"
      show-icon
    />
  </el-card>
</template>

<style scoped>
.trader-panel { display: flex; flex-direction: column; gap: 10px; }
.header-bar { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.title { font-size: 15px; font-weight: 600; }
.price-matrix {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
}
.pm-cell { text-align: center; padding: 12px 8px; border-radius: 6px; color: #fff; }
.pm-entry { background: var(--accent); }
.pm-stop { background: var(--danger); }
.pm-t1 { background: var(--success); }
.pm-t2 { background: var(--success); filter: brightness(0.85); }
.pm-label { font-size: 12px; opacity: 0.92; }
.pm-value { font-size: 19px; font-weight: 700; margin-top: 4px; }
.meta-row {
  display: flex; flex-wrap: wrap; gap: 18px; font-size: 13px;
  color: var(--text-secondary);
}
.meta-item strong { color: var(--text-primary); }
.reasoning {
  font-size: 13px; color: var(--text-primary); line-height: 1.6;
  background: var(--bg-tertiary); border-left: 3px solid var(--accent);
  padding: 8px 12px; border-radius: 0 6px 6px 0;
}
</style>
